import React from "react";
import {
  Box,
  Button,
  CssBaseline,
  Typography
} from "@mui/material";
import { useHistory } from "react-router-dom";

function AboutPage() {
  const history = useHistory();

  return (
    <>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#000000",
          color: "#FFFDF8",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* =====================================================
            HEADER
            ===================================================== */}
        <Box
          component="header"
          sx={{
            height: {
              xs: 90,
              md: 110
            },
            px: {
              xs: 3,
              md: 5
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #222"
          }}
        >
          {/* Logo */}
          <Box
            component="button"
            type="button"
            onClick={() => history.push("/")}
            aria-label="Return to Colloquia home"
            sx={{
              border: 0,
              padding: 0,
              margin: 0,
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            <Box
              component="img"
              src="/Colloquia_Logo.png"
              alt="Colloquia"
              sx={{
                height: {
                  xs: 62,
                  md: 72
                },
                width: "auto",
                borderRadius: "12px",
                objectFit: "cover"
              }}
            />
          </Box>

          {/* Return to landing page */}
          <Button
            onClick={() => history.push("/")}
            sx={{
              color: "#888",
              fontSize: "0.8rem",
              fontWeight: 300,
              letterSpacing: "0.06em",
              textTransform: "none",
              "&:hover": {
                color: "#FFFDF8",
                backgroundColor: "transparent"
              }
            }}
          >
            Return Home
          </Button>
        </Box>

        {/* =====================================================
            MAIN CONTENT
            ===================================================== */}
        <Box
          component="main"
          sx={{
            width: "100%",
            maxWidth: 1000,
            mx: "auto",
            px: {
              xs: 4,
              sm: 6,
              md: 8
            },
            py: {
              xs: 9,
              md: 14
            }
          }}
        >
          {/* Eyebrow */}
          <Typography
            sx={{
              color: "#888",
              fontSize: {
                xs: "0.75rem",
                md: "0.8rem"
              },
              fontWeight: 400,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              mb: 3
            }}
          >
            About Colloquia
          </Typography>

          {/* Main heading */}
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: "2.5rem",
                sm: "3.4rem",
                md: "4.5rem"
              },
              lineHeight: 1.05,
              fontWeight: 300,
              letterSpacing: "-0.035em",
              maxWidth: 850,
              mb: 5
            }}
          >
            The Intellectual Network.
          </Typography>

          {/* Intro */}
          <Typography
            sx={{
              fontSize: {
                xs: "1.15rem",
                md: "1.4rem"
              },
              lineHeight: 1.8,
              fontWeight: 300,
              color: "#E5E5E5",
              maxWidth: 800,
              mb: 12
            }}
          >
            Colloquia is a social media platform built around ideas,
            intellectual achievement, and meaningful participation.
            Our purpose is simple: to create a social environment where
            thoughtful people and high-quality ideas can receive the
            attention and influence they deserve.
          </Typography>

          {/* =====================================================
              SECTION: WHAT IS COLLOQUIA
              ===================================================== */}
          <Box
            component="section"
            sx={{
              borderTop: "1px solid #292929",
              pt: 6,
              mb: 10
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: {
                  xs: "1.7rem",
                  md: "2rem"
                },
                fontWeight: 300,
                mb: 4
              }}
            >
              What is Colloquia?
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800,
                mb: 3
              }}
            >
              Colloquia brings together the familiar forms of social
              media—discussion, publishing, voting, discovery, and
              community—within a platform designed to place greater
              emphasis on intellectual contribution.
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800
              }}
            >
              Rather than treating every interaction as equally
              informative, Colloquia is designed around the idea that
              a person's demonstrated intellectual achievements can
              provide useful information about the weight their
              contributions should carry within the community.
            </Typography>

          </Box>

          {/* =====================================================
              SECTION: WHY WE BUILT IT
              ===================================================== */}
          <Box
            component="section"
            sx={{
              borderTop: "1px solid #292929",
              pt: 6,
              mb: 10
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: {
                  xs: "1.7rem",
                  md: "2rem"
                },
                fontWeight: 300,
                mb: 4
              }}
            >
              Why we built it
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800,
                mb: 3
              }}
            >
              Modern social platforms are extraordinarily effective at
              distributing information. But popularity and intellectual
              quality are not necessarily the same thing.
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800,
                mb: 3
              }}
            >
              Colloquia was created to explore a different model: a
              social network in which intellectual accomplishment can
              play a meaningful role in determining whose contributions
              receive greater visibility and influence.
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800
              }}
            >
              We believe that the people who devote themselves to
              mathematics, science, engineering, medicine, philosophy,
              economics, history, law, literature, computer science,
              politics, and other fields of serious inquiry should have
              a meaningful place in the social conversation.
            </Typography>
          </Box>

          {/* =====================================================
              SECTION: OUR APPROACH
              ===================================================== */}
          <Box
            component="section"
            sx={{
              borderTop: "1px solid #292929",
              pt: 6,
              mb: 10
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: {
                  xs: "1.7rem",
                  md: "2rem"
                },
                fontWeight: 300,
                mb: 4
              }}
            >
              Our approach
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800,
                mb: 3
              }}
            >
              Colloquia combines social interaction with systems
              designed to help distinguish signal from noise. Our
              platform is built around algorithms, voting mechanisms,
              and reputation signals intended to improve the discovery
              and distribution of valuable contributions.
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800
              }}
            >
              We are still building and refining these systems. As
              Colloquia develops, we intend to study how different
              mechanisms affect discussion, discovery, participation,
              and the distribution of influence across the network.
            </Typography>
          </Box>

          {/* =====================================================
              SECTION: PRINCIPLES
              ===================================================== */}
          <Box
            component="section"
            sx={{
              borderTop: "1px solid #292929",
              pt: 6,
              mb: 10
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: {
                  xs: "1.7rem",
                  md: "2rem"
                },
                fontWeight: 300,
                mb: 5
              }}
            >
              What we value
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr"
                },
                gap: {
                  xs: 5,
                  md: 7
                }
              }}
            >
              {[
                {
                  title: "Intellectualism",
                  text:
                    "We value curiosity, knowledge, disciplined reasoning, and the pursuit of understanding."
                },
                {
                  title: "Truth",
                  text:
                    "We want the platform to encourage accurate information, honest argument, and good-faith inquiry."
                },
                {
                  title: "Merit",
                  text:
                    "We believe demonstrated achievement and substantive contribution should matter."
                },
                {
                  title: "Discourse",
                  text:
                    "We want disagreement to produce better ideas rather than simply more hostility."
                }
              ].map(({ title, text }) => (
                <Box key={title}>
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 400,
                      mb: 1.5,
                      color: "#FFFDF8"
                    }}
                  >
                    {title}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.98rem",
                      lineHeight: 1.8,
                      fontWeight: 300,
                      color: "#999"
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* =====================================================
              SECTION: COMPANY
              ===================================================== */}
          <Box
            component="section"
            sx={{
              borderTop: "1px solid #292929",
              pt: 6,
              mb: 10
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: {
                  xs: "1.7rem",
                  md: "2rem"
                },
                fontWeight: 300,
                mb: 4
              }}
            >
              The company
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800,
                mb: 3
              }}
            >
              Colloquia is developed and operated by Penneferies Squire
              Capital. We are building Colloquia as a long-term project
              focused on the intersection of technology, social networks,
              algorithms, and intellectual culture.
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },
                lineHeight: 1.9,
                fontWeight: 300,
                color: "#B8B8B8",
                maxWidth: 800
              }}
            >
              Colloquia is an evolving platform, and we welcome thoughtful
              criticism, ideas, and feedback from the people who use it.
            </Typography>
          </Box>

          {/* =====================================================
              FINAL STATEMENT
              ===================================================== */}
          <Box
            component="section"
            sx={{
              borderTop: "1px solid #292929",
              pt: 7,
              pb: 5
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: "1.35rem",
                  md: "1.7rem"
                },
                lineHeight: 1.6,
                fontWeight: 300,
                color: "#FFFDF8",
                maxWidth: 800
              }}
            >
              We are building a different kind of social network—one
              where ideas matter, intellectual achievement matters, and
              thoughtful people have a greater opportunity to be heard.
            </Typography>
          </Box>
        </Box>

        {/* =====================================================
            FOOTER
            ===================================================== */}
        <Box
          component="footer"
          sx={{
            mt: "auto",
            minHeight: 100,
            bgcolor: "#000",
            borderTop: "1px solid #222",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: {
              xs: 3,
              md: 5
            },
            py: 2,
            gap: 2,
            flexWrap: "wrap"
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: {
                xs: 0.5,
                sm: 1
              }
            }}
          >
            {[
              { label: "About", path: "/about" },
              { label: "Terms", path: "/terms" },
              { label: "Privacy", path: "/privacy" },
              { label: "Careers", path: "/careers" },
              { label: "Support", path: "/support" },
              { label: "Contact", path: "/contact" }
            ].map(({ label, path }) => (
              <Button
                key={label}
                onClick={() => history.push(path)}
                sx={{
                  color:
                    label === "About"
                      ? "#FFFDF8"
                      : "#777",
                  minWidth: "auto",
                  px: {
                    xs: 1,
                    sm: 1.25
                  },
                  py: 0.75,
                  fontSize: {
                    xs: "0.72rem",
                    sm: "0.8rem"
                  },
                  fontWeight: 300,
                  letterSpacing: "0.04em",
                  textTransform: "none",
                  borderRadius: "6px",

                  "&:hover": {
                    color: "#FFFDF8",
                    backgroundColor: "transparent"
                  }
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <Typography
            sx={{
              color: "#666",
              fontSize: {
                xs: "0.7rem",
                sm: "0.8rem"
              },
              fontWeight: 300,
              whiteSpace: {
                xs: "normal",
                sm: "nowrap"
              },
              textAlign: {
                xs: "center",
                sm: "right"
              }
            }}
          >
            © 2026 Colloquia from Penneferies Squire Capital
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default AboutPage;